import { clamp, throttle } from 'lodash-es'
import React, { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Caret } from '~/components/Icons'
import { ChangeSlide } from '~/components/Photos'
import type { HTMLElementProps } from '~/types'
import { classnames, cn, range, useWindowSize } from '~/utils'
import { CarouselItem } from './CarouselItem'

type Props = HTMLElementProps<
	'ul',
	{
		children: ReactNode[]
		snap?: 'snap-start' | 'snap-center' | 'snap-end'
		scrollMargin?: `scroll-m-${number}`
		scrollDistance?: 'page' | 'single'
	}
>

export const Carousel: React.FC<Props> = ({
	className,
	children,
	snap,
	scrollMargin = 'scroll-m-2',
	scrollDistance = 'page',
	...props
}) => {
	const [index, setIndex] = useState(0)
	const [showControls, setControls] = useState(true)
	const screen = useWindowSize()
	const [itemsVisible, setVisibleItems] = useState(1)
	const controlsRef = useRef<HTMLDivElement>(null)
	const [listElem, setListElem] = useState<HTMLUListElement | null>(null)

	const ratio = (controlsRef.current?.offsetWidth ?? 1) / (listElem?.scrollWidth ?? 1)

	const ref = useCallback<(_: HTMLUListElement | null) => void>(
		node => {
			if (node === null) return
			setListElem(node)
			setControls(node.scrollWidth > screen.width)

			const result = Array.from(node.children).findIndex(
				c => (c as HTMLElement).offsetLeft + 0.88 * c.scrollWidth >= node.offsetWidth
			)
			setVisibleItems(result)
		},
		[screen.width]
	)

	useLayoutEffect(() => {
		if (listElem === null) return
		const onScroll = throttle(async () => {
			let next = 0

			switch (snap) {
				case 'snap-end':
				case 'snap-start':
					for (let i = 0; i < listElem.children.length; i++) {
						const baseX = snap === 'snap-end' ? (listElem.firstElementChild as HTMLElement).offsetLeft : 0
						const child = listElem.children[i] as HTMLElement
						const threshold = child.offsetLeft - child.offsetWidth - baseX - 5
						if (listElem.scrollLeft >= threshold) {
							next = i
						}
					}
					break

				case 'snap-center':
				case undefined:
					next = (listElem.scrollLeft / (listElem.scrollWidth - listElem.offsetWidth)) * (children.length - 1)
					next = Math.round(next)
					break
			}

			setIndex(clamp(next, 0, children.length - 1))
		}, 1000 / 144)

		listElem.addEventListener('scroll', onScroll, { passive: true })
		return () => listElem.removeEventListener('scroll', onScroll)
	}, [listElem, screen.width, snap, children, ratio])

	function scrollTo(i: number, align?: Props['snap']) {
		if (listElem === null) return i
		const length = listElem.children.length - 1
		i = clamp(i, 0, length)
		let target: number
		switch (align) {
			case 'snap-start':
				target = (listElem.children[i] as HTMLElement)?.offsetLeft
				break

			case 'snap-end':
				target = (i * (listElem.scrollWidth - listElem.offsetWidth)) / length
				break

			case 'snap-center':
			case undefined:
				target = (i * (listElem.scrollWidth - listElem.offsetWidth)) / length
				break
		}
		listElem.scrollTo({ left: target, behavior: 'smooth' })
		return i
	}

	return (
		<div className="relative">
			<ul
				className={classnames(
					'no-scrollbar list flex snap-mandatory flex-row items-stretch overflow-x-scroll scroll-smooth',
					{ 'snap-x': snap, 'justify-center': !showControls },
					className
				)}
				ref={ref}
				{...props}
			>
				{children.map((child, i) => (
					<CarouselItem key={i} className={classnames(snap, scrollMargin, 'select-none')}>
						{child}
					</CarouselItem>
				))}
			</ul>
			{showControls && (
				<div className="absolute bottom-0 flex w-full items-center justify-between px-8 leading-[0]">
					<ChangeSlide
						className="flex-min static flex items-center rounded p-4"
						onClick={_ => scrollTo(index - (scrollDistance === 'single' ? 1 : itemsVisible), snap)}
					>
						<Caret className="rotate-90" />
					</ChangeSlide>
					<div className="inline-grid flex-grow grid-flow-row grid-cols-[repeat(auto-fit,1rem)] justify-center gap-1.5">
						{range(children.length - 1).map(i => (
							<button
								key={i}
								className={cn(
									'button h-4 rounded-full border-current text-inherit',
									'hover:bg-current hover:drop-shadow',
									i >= index && i < index + itemsVisible ? 'bg-current' : 'bg-gray-400'
								)}
								onClick={() => scrollTo(i, snap)}
							/>
						))}
					</div>
					<ChangeSlide
						className="flex-min static flex items-center rounded p-4"
						onClick={_ => scrollTo(index + (scrollDistance === 'single' ? 1 : itemsVisible), snap)}
					>
						<Caret className="-rotate-90" />
					</ChangeSlide>
				</div>
			)}
		</div>
	)
}
